import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

enum VerificationStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error'
}

export default function EmailVerification() {
  const [status, setStatus] = useState<VerificationStatus>(VerificationStatus.LOADING);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        // The URL will contain a hash with the token information
        // Supabase's detectSessionInUrl will automatically process this
        // We just need to check if it was successful
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          setStatus(VerificationStatus.ERROR);
          setErrorMessage(error.message);
          return;
        }
        
        if (data.session) {
          // Session exists, verification was successful
          setStatus(VerificationStatus.SUCCESS);
          
          // Auto-redirect to login after 5 seconds
          setTimeout(() => {
            navigate('/login');
          }, 5000);
        } else {
          // No session, verification failed or expired
          setStatus(VerificationStatus.ERROR);
          setErrorMessage('Verification link is invalid or has expired.');
        }
      } catch (err) {
        setStatus(VerificationStatus.ERROR);
        setErrorMessage('An unexpected error occurred during verification.');
        console.error('Verification error:', err);
      }
    };

    handleEmailVerification();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className="mx-auto h-16 w-auto"
          src="/assets/araclogo2.png"
          alt="ARAC Logo"
        />
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Email Verification
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === VerificationStatus.LOADING && (
            <div className="text-center py-6">
              <Loader className="h-12 w-12 text-primary mx-auto animate-spin" />
              <p className="mt-4 text-gray-600">Verifying your email...</p>
            </div>
          )}

          {status === VerificationStatus.SUCCESS && (
            <div className="text-center py-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h3 className="mt-4 text-xl font-medium text-gray-900">Email Verified Successfully!</h3>
              <p className="mt-2 text-gray-600">
                Your email has been verified. You can now sign in to your account.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                You will be redirected to the login page in 5 seconds.
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Go to Login
                </Link>
              </div>
            </div>
          )}

          {status === VerificationStatus.ERROR && (
            <div className="text-center py-6">
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h3 className="mt-4 text-xl font-medium text-gray-900">Verification Failed</h3>
              <p className="mt-2 text-gray-600">
                {errorMessage || 'There was a problem verifying your email.'}
              </p>
              <div className="mt-6 space-y-3">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Go to Login
                </Link>
                <p className="text-sm text-gray-500">
                  If you continue to have issues, please contact support.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
